import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../App';
import toast from 'react-hot-toast';
import { sanitizeSources } from '../utils/whitelabel';

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'proposal_sent', 'won', 'lost'];
const STATUS_COLORS = { new: '#0284C7', contacted: '#8B5CF6', qualified: '#F59E0B', proposal_sent: '#EC4899', won: '#16A34A', lost: '#DC2626' };
const STATUS_LABELS = { new: 'New Lead', contacted: 'Contacted', qualified: 'Qualified', proposal_sent: 'Proposal Sent', won: 'Won / Closed', lost: 'Lost' };

export default function LeadDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichment, setEnrichment] = useState(null);
  const [contactEnriching, setContactEnriching] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [solarAnalyzing, setSolarAnalyzing] = useState(false);
  const [permitLoading, setPermitLoading] = useState(false);
  const [permitHistory, setPermitHistory] = useState(null);
  const [reachOut, setReachOut] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [intel, setIntel] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [flyerSending, setFlyerSending] = useState(false);
  const [mailPieces, setMailPieces] = useState([]);

  const loadIntel = async () => {
    try {
      const res = await api.post(`/api/leads/${id}/intelligence`);
      setIntel(res.data);
    } catch { /* non-blocking */ }
  };
  const loadTimeline = async () => {
    try {
      const res = await api.get(`/api/leads/${id}/timeline`);
      setTimeline(res.data);
    } catch { /* non-blocking */ }
  };
  const loadMail = async () => {
    try {
      const res = await api.get(`/api/mail?lead_id=${id}`);
      setMailPieces(res.data.mail || []);
    } catch { /* non-blocking */ }
  };

  const handleSendFlyer = async () => {
    setFlyerSending(true);
    try {
      const res = await api.post('/api/mail/send', { lead_id: id });
      if (res.data.mode === 'simulated') {
        toast.success('Flyer queued (SIMULATED — configure mail delivery to render a real proof)');
      } else {
        toast.success(`Flyer sent — status: ${res.data.status}`);
      }
      loadMail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Flyer send failed'); }
    finally { setFlyerSending(false); }
  };

  const [waterfalling, setWaterfalling] = useState(false);
  const [propEnriching, setPropEnriching] = useState(false);
  const [freeEnriching, setFreeEnriching] = useState(false);
  const handleFreeEnrich = async () => {
    setFreeEnriching(true);
    try {
      const res = await api.post(`/api/leads/${id}/free-enrich`);
      const fe = res.data;
      setLead(prev => ({
        ...prev,
        owner_name: fe.owner_name || prev.owner_name,
        mailing_address: fe.mailing_address || prev.mailing_address,
        is_absentee_owner: fe.is_absentee_owner,
        property_value: fe.property_value || prev.property_value,
        year_built: fe.year_built || prev.year_built,
        property_type: fe.property_type || prev.property_type,
        solar_score: fe.rescored?.solar ?? prev.solar_score,
        roofing_score: fe.rescored?.roofing ?? prev.roofing_score,
      }));
      if (fe.is_absentee_owner) toast.success('Absentee owner detected — prime direct-mail target');
      else if (fe.sources?.length) toast.success(`Enriched from ${fe.sources.length} free source(s)`);
      else toast(fe.note || 'No extra free data found', { icon: 'ℹ️' });
      loadIntel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Free enrichment failed'); }
    finally { setFreeEnriching(false); }
  };
  const handlePropertyData = async () => {
    setPropEnriching(true);
    try {
      const res = await api.post('/api/enrich/property-data', { lead_id: id });
      if (res.data.found) {
        setLead(prev => ({
          ...prev,
          property_value: res.data.property_value || prev.property_value,
          year_built: res.data.year_built || prev.year_built,
          sqft: res.data.sqft || prev.sqft,
          property_type: res.data.property_type || prev.property_type,
          solar_score: res.data.rescored?.solar ?? prev.solar_score,
          roofing_score: res.data.rescored?.roofing ?? prev.roofing_score,
        }));
        toast.success(`Property data added · re-scored (solar ${res.data.rescored?.solar}, roof ${res.data.rescored?.roofing})`);
        loadIntel();
      } else {
        toast('No property record found for this address', { icon: 'ℹ️' });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Property enrichment failed'); }
    finally { setPropEnriching(false); }
  };
  const handleWaterfall = async () => {
    setWaterfalling(true);
    try {
      const res = await api.post(`/api/leads/${id}/waterfall-enrich`);
      const wf = res.data;
      setLead(prev => ({
        ...prev,
        email: wf.best_email || prev.email,
        phone: wf.best_phone || prev.phone,
        phone_callable: wf.phone_callable,
        phone_line_type: wf.phone_line_type,
        phone_carrier: wf.phone_carrier,
        email_verified: wf.email_verified,
        contact_confidence: wf.confidence,
        contact_status: wf.status,
        contact_note: wf.note,
        waterfall: wf,
      }));
      if (wf.status === 'verified') toast.success(`Verified contact found (${wf.confidence}% confidence)`);
      else if (wf.status === 'unverified_phone') toast('Phone found but not confirmed as a live mobile', { icon: '⚠️' });
      else if (wf.status === 'unverified_email') toast('Only a pattern-guessed email is available — not verified', { icon: '⚠️' });
      else toast(wf.note || 'No verified contact found', { icon: 'ℹ️' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Waterfall enrichment failed'); }
    finally { setWaterfalling(false); }
  };

  const handleReachOut = async () => {
    setReachOut(true);
    try {
      const res = await api.post(`/api/leads/${id}/enrich-full`);
      setOutreach(res.data);
      setLead(prev => ({
        ...prev,
        owner_name: res.data.owner_name,
        email: res.data.reachability?.top_email || prev.email,
        phone: res.data.reachability?.best_phone || prev.phone,
        mailing_address: res.data.mailing_address || prev.mailing_address,
        is_absentee: res.data.reachability?.is_absentee,
        outreach_copy: res.data.outreach_copy,
        reachability: res.data.reachability,
      }));
      setTab('outreach');
      const ch = res.data.reachability?.channels || [];
      toast.success(ch.length ? `Reachable via ${ch.join(', ')}` : 'Enriched — building outreach');
    } catch { toast.error('Enrichment failed'); }
    finally { setReachOut(false); }
  };

  useEffect(() => {
    api.get(`/api/leads/${id}`).then(res => {
      setLead(res.data); setEditData(res.data); setLoading(false);
      if (res.data.ai_enrichment) setEnrichment(res.data.ai_enrichment);
      if (res.data.enrichment) setContactData(res.data.enrichment);
      if (res.data.shovels_enrichment) setPermitHistory(res.data.shovels_enrichment);
    }).catch(() => { toast.error('Lead not found'); navigate('/leads'); });
    loadIntel();
    loadTimeline();
    loadMail();
  }, [id, navigate]);

  const handlePermitHistory = async () => {
    setPermitLoading(true);
    try {
      const res = await api.post('/api/enrich/shovels-permits', {
        address: lead.address, city: lead.city, state: lead.state, zip: lead.zip, lead_id: lead.id || id,
      });
      setPermitHistory(res.data);
      if (res.data.owner_name && (!lead.owner_name || String(lead.name || '').startsWith('Owner at'))) {
        setLead(prev => ({ ...prev, owner_name: res.data.owner_name, name: res.data.owner_name, phone: prev.phone || res.data.contact?.phone, email: prev.email || res.data.contact?.email }));
      }
      if (res.data.quota_exceeded) {
        toast.error(res.data.note);
      } else if (!res.data.found) {
        toast(res.data.residents?.length ? `No permits on file — ${res.data.residents.length} resident record(s) found` : 'No permit records found for this address', { icon: 'ℹ️' });
      } else if (res.data.has_existing_solar) {
        toast.error('⚠️ This home already has a solar permit (competitor)');
      } else {
        setLead(prev => ({ ...prev, has_existing_solar: res.data.has_existing_solar }));
        toast.success(`${res.data.permit_count} permits found nationwide · ${res.data.tags.length} signals`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Permit lookup failed');
    } finally { setPermitLoading(false); }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const res = await api.post(`/api/leads/${id}/enrich`);
      setEnrichment(res.data.enrichment);
      toast.success('AI enrichment complete!');
    } catch { toast.error('Enrichment failed'); }
    finally { setEnriching(false); }
  };

  const handleContactEnrich = async () => {
    setContactEnriching(true);
    try {
      const res = await api.post(`/api/leads/${id}/enrich-v3`);
      if (res.data.status === 'no_owner_data') {
        toast.error('No owner data available for this property');
      } else {
        setContactData({
          top_email: res.data.top_email,
          top_3_emails: res.data.top_3_emails,
          phone_intelligence: res.data.phone_intelligence,
          cross_references: res.data.cross_references || [],
          verification_steps: res.data.verification_steps || [],
          data_sources: res.data.data_sources || [],
          property_details: res.data.property_details || {},
          assessment: res.data.assessment || {},
        });
        setLead(prev => ({
          ...prev,
          owner_name: res.data.owner_name,
          email: res.data.top_email || prev.email,
          ai_outreach_strategy: res.data.ai_outreach_strategy,
          accuracy_score: res.data.accuracy_score,
        }));
        toast.success(`v3 Enrichment: ${res.data.owner_name} (${res.data.accuracy_score}% accuracy, ${res.data.data_sources?.length || 0} sources)`);
      }
    } catch { toast.error('Contact enrichment failed'); }
    finally { setContactEnriching(false); }
  };

  const handleSave = async () => {
    try {
      const res = await api.put(`/api/leads/${id}`, editData);
      setLead({ ...res.data, activities: lead.activities }); setEditing(false);
      toast.success('Lead updated!');
    } catch { toast.error('Failed to update'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post('/api/activities', { type: 'note', lead_name: lead.name, lead_id: id, description: newNote });
      const res = await api.get(`/api/leads/${id}`);
      setLead(res.data); setNewNote('');
      toast.success('Note added!');
    } catch { toast.error('Failed to add note'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try { await api.delete(`/api/leads/${id}`); toast.success('Lead deleted'); navigate('/leads'); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;
  if (!lead) return null;

  const scorePct = lead.score || 0;
  const scoreLabel = scorePct >= 80 ? 'Hot Lead' : scorePct >= 60 ? 'Warm Lead' : scorePct >= 40 ? 'Lukewarm' : 'Cold Lead';
  const scoreGrad = scorePct >= 80 ? '#16A34A' : scorePct >= 60 ? '#F97316' : scorePct >= 40 ? '#0284C7' : '#DC2626';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-details-page">
      <div className="flex items-center gap-2 text-sm text-text-muted">
        <Link to="/leads" className="hover:text-primary transition-colors" data-testid="back-btn"><i className="fa-solid fa-arrow-left mr-1" />Leads</Link>
        <i className="fa-solid fa-chevron-right text-[10px]" />
        <span className="text-text-primary font-medium">{lead.name}</span>
      </div>

      <div className="flex flex-col xl:flex-row gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-4">
          {/* Header Card */}
          <div className="card-interactive p-6">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xl font-bold">{lead.name?.charAt(0)}</div>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-text-primary" data-testid="lead-name">{lead.name}{lead.is_demo && <span className="ml-3 align-middle px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider" data-testid="demo-badge">Demo record</span>}</h1>
                  <p className="text-sm text-text-muted mt-0.5"><i className="fa-solid fa-location-dot mr-1" />{lead.address}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleReachOut} disabled={reachOut} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="reach-out-btn">
                  {reachOut ? <><i className="fa-solid fa-spinner fa-spin" /> Working...</> : <><i className="fa-solid fa-bullhorn" /> Reach Out (Free)</>}
                </button>
                <button onClick={handleContactEnrich} disabled={contactEnriching} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="enrich-contact-btn">
                  {contactEnriching ? <><i className="fa-solid fa-spinner fa-spin" /> Finding...</> : <><i className="fa-solid fa-address-book" /> Find Owner</>}
                </button>
                <button onClick={handleEnrich} disabled={enriching} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-primary to-emerald-600 text-white text-xs font-bold shadow-glow-green hover:shadow-lg transition-all disabled:opacity-50" data-testid="enrich-lead-btn">
                  {enriching ? <><i className="fa-solid fa-spinner fa-spin" /> Enriching...</> : <><i className="fa-solid fa-brain" /> AI Enrich</>}
                </button>
                <button onClick={async () => {
                  setSolarAnalyzing(true);
                  try {
                    const res = await api.post('/api/solar/analyze', { address: lead.address, lead_id: lead.id });
                    setLead(prev => ({ ...prev, ...res.data, solar_analysis: res.data, google_solar_analyzed: true }));
                    toast.success(`Solar analysis complete! ${res.data.solar_potential} potential, ${res.data.annual_production_kwh?.toLocaleString()} kWh/yr`);
                  } catch { toast.error('Solar analysis failed'); }
                  setSolarAnalyzing(false);
                }} disabled={solarAnalyzing} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="solar-analyze-btn">
                  {solarAnalyzing ? <><i className="fa-solid fa-spinner fa-spin" /> Analyzing...</> : <><i className="fa-solid fa-sun" /> Roof Analysis</>}
                </button>
                <button onClick={handlePermitHistory} disabled={permitLoading} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="permit-history-btn">
                  {permitLoading ? <><i className="fa-solid fa-spinner fa-spin" /> Searching...</> : <><i className="fa-solid fa-file-contract" /> Permit History</>}
                </button>
                <button onClick={handleSendFlyer} disabled={flyerSending} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="send-flyer-btn">
                  {flyerSending ? <><i className="fa-solid fa-spinner fa-spin" /> Sending...</> : <><i className="fa-solid fa-envelope-open-text" /> Send Flyer</>}
                </button>
                <button onClick={handleWaterfall} disabled={waterfalling} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="waterfall-btn">
                  {waterfalling ? <><i className="fa-solid fa-spinner fa-spin" /> Enriching...</> : <><i className="fa-solid fa-water" /> OSINT Waterfall</>}
                </button>
                <button onClick={handleFreeEnrich} disabled={freeEnriching} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-lime-500 to-green-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="free-enrich-btn">
                  {freeEnriching ? <><i className="fa-solid fa-spinner fa-spin" /> Enriching...</> : <><i className="fa-solid fa-gift" /> Free Enrich</>}
                </button>
                <button onClick={handlePropertyData} disabled={propEnriching} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-xs font-bold shadow hover:shadow-lg transition-all disabled:opacity-50" data-testid="property-data-btn">
                  {propEnriching ? <><i className="fa-solid fa-spinner fa-spin" /> Loading...</> : <><i className="fa-solid fa-building-circle-check" /> Property Data</>}
                </button>
                <Link to={`/proposals?lead_id=${lead.id}`} className="btn-primary text-xs py-2"><i className="fa-solid fa-file-invoice mr-1.5" />Generate Proposal</Link>
                <button onClick={() => setEditing(!editing)} className="btn-secondary text-xs py-2" data-testid="edit-lead-btn">
                  <i className={`fa-solid ${editing ? 'fa-xmark' : 'fa-pen'} mr-1`} />{editing ? 'Cancel' : 'Edit'}
                </button>
                <button onClick={handleDelete} className="p-2 rounded-xl border border-destructive/20 text-destructive hover:bg-destructive-light transition-all" data-testid="delete-lead-btn">
                  <i className="fa-solid fa-trash text-xs" />
                </button>
              </div>
            </div>

            {/* Status Pipeline */}
            <div className="flex gap-1 mb-6">
              {STATUS_OPTIONS.map((s, i) => {
                const current = STATUS_OPTIONS.indexOf(editing ? editData.status : lead.status);
                const idx = i;
                const active = s === (editing ? editData.status : lead.status);
                const past = idx <= current;
                return (
                  <button key={s} onClick={() => editing && setEditData({ ...editData, status: s })} className={`flex-1 py-2 rounded-lg text-[11px] font-semibold transition-all border ${active ? 'text-white shadow-sm' : past ? 'text-white/80' : 'bg-surface-alt text-text-muted border-border'}`} style={active || past ? { background: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] } : {}} data-testid={`status-${s}`}>
                    {STATUS_LABELS[s]}
                  </button>
                );
              })}
            </div>

            {/* Contact Info Grid */}
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[{ k: 'email', l: 'Email', ph: 'email@example.com' }, { k: 'phone', l: 'Phone', ph: '555-0123' }, { k: 'city', l: 'City' }, { k: 'state', l: 'State' }].map(f => (
                    <div key={f.k}><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{f.l}</label><input value={editData[f.k] || ''} onChange={e => setEditData({ ...editData, [f.k]: e.target.value })} className="input-field h-9 text-sm" placeholder={f.ph} data-testid={`edit-${f.k}`} /></div>
                  ))}
                </div>
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Notes</label><textarea value={editData.notes || ''} onChange={e => setEditData({ ...editData, notes: e.target.value })} rows={3} className="input-field h-auto py-2.5 resize-none" data-testid="edit-notes" /></div>
                <button onClick={handleSave} className="btn-primary text-sm" data-testid="save-lead-btn">Save Changes</button>
              </div>
            ) : (
              <>
              {lead.is_absentee_owner && (
                <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold" data-testid="absentee-badge">
                  <i className="fa-solid fa-plane-departure" /> Absentee owner — mailing address differs from the property (out-of-area / investor). Prime direct-mail target.
                </div>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: 'fa-envelope', l: 'Email', v: lead.email || (lead.email_guess ? `${lead.email_guess} (unverified guess)` : '') },
                  { icon: 'fa-phone', l: 'Phone', v: lead.phone || (lead.phone_area_code ? `area code ${lead.phone_area_code} — no verified number` : '') },
                  { icon: 'fa-location-dot', l: 'Location', v: `${lead.city}, ${lead.state} ${lead.zip}` },
                  { icon: 'fa-house', l: 'Property', v: lead.property_type },
                  { icon: 'fa-link', l: 'Source', v: (lead.source || '').replace('_', ' ') },
                  { icon: 'fa-user', l: 'Owner', v: lead.owner_name || lead.assigned_to || 'Unknown' },
                  ...(lead.is_absentee_owner ? [{ icon: 'fa-envelope-open-text', l: 'Mailing Addr', v: lead.mailing_address || 'N/A' }] : []),
                ].map(item => (
                  <div key={item.l} className="flex items-center gap-3 p-3 rounded-xl bg-surface-alt">
                    <div className="w-9 h-9 rounded-lg bg-surface border border-border flex items-center justify-center flex-shrink-0"><i className={`fa-solid ${item.icon} text-text-muted text-xs`} /></div>
                    <div><p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{item.l}</p><p className="text-sm font-medium text-text-primary capitalize">{item.v || 'N/A'}</p></div>
                  </div>
                ))}
              </div>
              </>
            )}
            {!editing && (lead.phone || lead.email) && (
              <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="callable-gate">
                {lead.phone_callable ? (
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold shadow hover:shadow-lg transition-all" data-testid="call-btn">
                    <i className="fa-solid fa-phone-volume" /> Call {lead.phone}
                  </a>
                ) : lead.phone ? (
                  <span className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface-alt border border-border text-text-muted text-sm font-medium cursor-not-allowed" data-testid="call-disabled" title="Phone not yet verified as a callable mobile — run OSINT Waterfall">
                    <i className="fa-solid fa-phone-slash" /> {lead.phone} · not verified callable
                  </span>
                ) : null}
                {lead.email && (
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface border border-border text-text-primary text-sm font-medium hover:border-primary transition-all" data-testid="email-btn">
                    <i className="fa-solid fa-envelope" /> {lead.email}
                    {lead.email_verified === false && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid="email-unverified-badge">guess</span>
                    )}
                  </a>
                )}
                {(lead.phone_line_type || lead.phone_carrier) && (
                  <span className="text-[11px] text-text-muted">
                    {lead.phone_line_type && <span className="capitalize font-semibold">{lead.phone_line_type}</span>}
                    {lead.phone_carrier ? ` · ${lead.phone_carrier}` : ''}
                    {lead.contact_confidence ? ` · ${lead.contact_confidence}% confidence` : ''}
                  </span>
                )}
              </div>
            )}
            {!editing && lead.contact_note && (lead.contact_status === 'none' || lead.contact_status === 'unverified_email') && (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="contact-note">
                <i className="fa-solid fa-circle-info mt-0.5" />
                <span>{lead.contact_note}</span>
              </div>
            )}
            {lead.notes && !editing && (
              <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/10">
                <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Notes</p>
                <p className="text-sm text-text-primary">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* Nationwide Permit History (Shovels) */}
          {permitHistory && permitHistory.found && (
            <div className="card-interactive p-6" data-testid="permit-history-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center"><i className="fa-solid fa-file-contract text-teal-600 text-sm" /></div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-text-primary">Nationwide Permit History</h3>
                    <p className="text-[11px] text-text-muted">{permitHistory.permit_count} permit(s) · {permitHistory.matched_address}</p>
                  </div>
                </div>
                {permitHistory.has_existing_solar ? (
                  <span className="badge bg-red-50 text-red-700 border border-red-200"><i className="fa-solid fa-triangle-exclamation mr-1" />Existing Solar</span>
                ) : permitHistory.signal ? (
                  <span className="badge bg-green-50 text-green-700 border border-green-200"><i className="fa-solid fa-bolt mr-1" />{permitHistory.signal}</span>
                ) : null}
              </div>
              {permitHistory.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {permitHistory.tags.map(t => (
                    <span key={t} className="px-2.5 py-1 rounded-full bg-teal-50 border border-teal-100 text-[10px] font-bold text-teal-700 uppercase tracking-wide">{t.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              )}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {permitHistory.permits?.map((p, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-surface-alt" data-testid={`permit-row-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">{p.type || 'Permit'}</p>
                      {p.description && <p className="text-[11px] text-text-muted truncate">{p.description}</p>}
                      <p className="text-[10px] text-text-muted mt-0.5">{p.jurisdiction}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[11px] font-mono text-text-primary">{p.issue_date || '—'}</p>
                      {p.job_value > 0 && <p className="text-[11px] font-bold text-primary">${p.job_value.toLocaleString()}</p>}
                      {p.status && <span className="text-[9px] uppercase tracking-wide text-text-muted">{p.status}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-text-muted mt-3 text-right">Source: {permitHistory.source}</p>
            </div>
          )}
          {permitHistory && !permitHistory.found && (
            <div className={`card-interactive p-4 text-center text-sm ${permitHistory.quota_exceeded ? 'text-amber-700 bg-amber-50 border border-amber-200' : 'text-text-muted'}`} data-testid="permit-history-empty">
              <i className={`fa-solid ${permitHistory.quota_exceeded ? 'fa-triangle-exclamation' : 'fa-file-circle-xmark'} mr-2 opacity-60`} />{permitHistory.quota_exceeded ? permitHistory.note : 'No permit records found for this address in the nationwide database.'}
            </div>
          )}
          {permitHistory?.residents?.length > 0 && (
            <div className="card-interactive p-5" data-testid="residents-panel">
              <div className="flex items-center justify-between mb-3">
                <div><h4 className="font-bold text-text-primary text-sm"><i className="fa-solid fa-people-roof mr-2 text-primary" />Who lives here</h4><p className="text-[11px] text-text-muted">Resident records for this property · homeowner flagged first</p></div>
                <span className="badge bg-primary/10 text-primary">{permitHistory.residents.length} on file</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {permitHistory.residents.map((r, i) => (
                  <div key={i} className={`p-3 rounded-xl border ${r.is_homeowner ? 'border-primary/40 bg-primary/5' : 'border-border bg-surface-alt'}`} data-testid={`resident-${i}`}>
                    <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-text-primary truncate">{r.name || 'Unnamed resident'}</p>{r.is_homeowner && <span className="text-[9px] font-bold uppercase tracking-widest text-primary">Homeowner</span>}</div>
                    {r.phone && <a href={`tel:${r.phone}`} className="block text-xs text-text-secondary mt-1 hover:text-primary"><i className="fa-solid fa-phone mr-1.5 text-[10px]" />{r.phone}</a>}
                    {r.emails?.[0] && <a href={`mailto:${r.emails[0]}`} className="block text-xs text-text-secondary mt-0.5 truncate hover:text-primary"><i className="fa-solid fa-envelope mr-1.5 text-[10px]" />{r.emails[0]}</a>}
                    {(r.income_range || r.net_worth) && <p className="text-[10px] text-text-muted mt-1.5">{[r.income_range && `Income ${r.income_range}`, r.net_worth && `Net worth ${r.net_worth}`].filter(Boolean).join(' · ')}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="card-interactive">
            <div className="flex gap-0 border-b border-border px-6">
              {['details', 'intelligence', 'outreach', 'activities', 'notes'].map(t => (
                <button key={t} onClick={() => setTab(t)} className={`px-5 py-3.5 text-sm font-medium capitalize transition-all border-b-2 ${tab === t ? 'text-primary border-primary' : 'text-text-muted border-transparent hover:text-text-primary'}`} data-testid={`tab-${t}`}>{t}</button>
              ))}
            </div>
            <div className="p-6">
              {tab === 'details' && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { l: 'Created', v: new Date(lead.created_at).toLocaleDateString() },
                    { l: 'Updated', v: new Date(lead.updated_at).toLocaleDateString() },
                    { l: 'Roof Age', v: lead.roof_age ? `${lead.roof_age} yrs` : 'N/A' },
                    { l: 'System Size', v: lead.recommended_system_kw ? `${lead.recommended_system_kw} kW` : 'N/A' },
                    { l: 'Annual kWh', v: lead.annual_kwh ? `${lead.annual_kwh.toLocaleString()}` : 'N/A' },
                    { l: 'Est. Savings', v: lead.estimated_savings ? `$${lead.estimated_savings.toLocaleString()}/yr` : 'N/A' },
                    { l: 'Est. Value', v: lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : 'N/A' },
                    { l: 'Solar Rating', v: lead.solar_potential || 'N/A' },
                  ].map(d => (
                    <div key={d.l} className="p-3 rounded-xl bg-surface-alt"><p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{d.l}</p><p className="text-sm font-semibold text-text-primary font-mono tabular-nums">{d.v}</p></div>
                  ))}
                </div>
              )}
              {tab === 'intelligence' && (
                <div className="space-y-6" data-testid="intelligence-tab">
                  {!intel ? (
                    <div className="text-center py-10 text-text-muted text-sm"><i className="fa-solid fa-spinner fa-spin mr-2" />Computing intelligence…</div>
                  ) : (
                    <>
                      {/* Score cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                          { key: 'solar', label: 'Solar Score', icon: 'fa-sun', from: 'from-amber-500', to: 'to-orange-500', data: intel.solar },
                          { key: 'roofing', label: 'Roofing Score', icon: 'fa-house-chimney', from: 'from-sky-500', to: 'to-blue-600', data: intel.roofing },
                          { key: 'overall', label: 'Opportunity', icon: 'fa-fire', from: 'from-emerald-500', to: 'to-green-600', data: intel.overall },
                        ].map(c => (
                          <div key={c.key} className={`rounded-2xl p-5 text-white bg-gradient-to-br ${c.from} ${c.to} shadow-lg`} data-testid={`score-card-${c.key}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold uppercase tracking-widest opacity-90"><i className={`fa-solid ${c.icon} mr-1.5`} />{c.label}</span>
                              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-white/25">{c.data.tier}</span>
                            </div>
                            <div className="mt-3 flex items-end gap-1"><span className="text-5xl font-black tabular-nums">{c.data.score}</span><span className="text-lg font-bold opacity-70 mb-1">/100</span></div>
                            {c.data.reason && <p className="text-[11px] opacity-90 mt-2 leading-snug">{c.data.reason}</p>}
                          </div>
                        ))}
                      </div>

                      {/* Overall breakdown */}
                      <div className="rounded-2xl border border-border p-5 bg-surface-alt" data-testid="score-breakdown">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-3">Why this opportunity score</p>
                        <div className="space-y-2">
                          {intel.overall.breakdown.map((b, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className="flex-1 h-6 rounded-lg bg-surface overflow-hidden relative">
                                <div className="h-full bg-emerald-500/80" style={{ width: `${Math.min(100, b.points * 4)}%` }} />
                                <span className="absolute inset-0 flex items-center px-3 text-xs font-medium text-text-primary">{b.label}</span>
                              </div>
                              <span className="text-sm font-mono font-bold text-emerald-600 w-10 text-right">+{b.points}</span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 border-t border-border mt-2"><span className="text-sm font-bold text-text-primary">Total</span><span className="text-sm font-black text-emerald-600">{intel.overall.score}/100</span></div>
                        </div>
                      </div>

                      {/* Cross-sell */}
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-3">Recommended Services (Cross-Sell)</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cross-sell-list">
                          {intel.cross_sell.map((s, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-surface">
                              <div className="relative w-11 h-11 flex-shrink-0">
                                <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90"><circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="4" /><circle cx="18" cy="18" r="15" fill="none" stroke="#16a34a" strokeWidth="4" strokeDasharray={`${s.confidence * 0.94} 94`} strokeLinecap="round" /></svg>
                                <span className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-text-primary">{s.confidence}%</span>
                              </div>
                              <div className="min-w-0"><p className="text-sm font-bold text-text-primary">{s.service}</p><p className="text-[11px] text-text-muted truncate">{s.reason}</p></div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Factor detail: solar + roofing */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[{ t: 'Solar factors', d: intel.solar }, { t: 'Roofing factors', d: intel.roofing }].map((col, ci) => (
                          <div key={ci} className="rounded-2xl border border-border p-4">
                            <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-2">{col.t}</p>
                            <div className="space-y-1.5">
                              {col.d.factors.map((f, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                  <span className="text-text-primary">{f.factor} {f.value && <span className="text-text-muted text-xs">· {f.value}</span>}</span>
                                  <span className={`font-mono font-bold ${f.sentiment === 'positive' ? 'text-emerald-600' : 'text-rose-500'}`}>{f.impact}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Property timeline */}
                      {timeline && timeline.event_count > 0 && (
                        <div className="rounded-2xl border border-border p-5" data-testid="property-timeline">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-3">Property Timeline · {timeline.event_count} permit(s)</p>
                          <div className="space-y-3">
                            {timeline.timeline.map((e, i) => (
                              <div key={i} className="flex gap-3">
                                <div className="flex flex-col items-center"><div className="w-3 h-3 rounded-full bg-primary" />{i < timeline.timeline.length - 1 && <div className="w-px flex-1 bg-border" />}</div>
                                <div className="pb-3 min-w-0">
                                  <p className="text-sm font-semibold text-text-primary capitalize">{(e.permit_type || 'Permit').replace(/_/g, ' ')}</p>
                                  {e.description && <p className="text-[11px] text-text-muted truncate">{e.description}</p>}
                                  <p className="text-[10px] text-text-muted mt-0.5 font-mono">{e.date ? new Date(e.date).toLocaleDateString() : '—'}{e.valuation ? ` · $${Number(e.valuation).toLocaleString()}` : ''}{e.contractor ? ` · ${e.contractor}` : ''}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Data quality + mailed flyers */}
                      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                        <span className="text-text-muted">Data confidence: <span className="font-bold text-text-primary">{intel.data_quality.confidence}%</span></span>
                        {mailPieces.length > 0 && <span className="text-text-muted">Flyers mailed: <span className="font-bold text-text-primary">{mailPieces.length}</span> ({mailPieces[0].mode})</span>}
                      </div>
                    </>
                  )}
                </div>
              )}
              {tab === 'activities' && (
                <div className="space-y-3">
                  {(lead.activities || []).length === 0 ? <p className="text-text-muted text-sm py-8 text-center">No activities yet</p> : (lead.activities || []).map((act, i) => (
                    <div key={act.id || i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-surface-alt transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-surface-alt border border-border flex items-center justify-center flex-shrink-0">
                        <i className={`fa-solid ${act.type === 'call' ? 'fa-phone text-primary' : act.type === 'email' ? 'fa-envelope text-accent' : act.type === 'note' ? 'fa-sticky-note text-secondary' : 'fa-info-circle text-text-muted'} text-xs`} />
                      </div>
                      <div><p className="text-sm text-text-primary">{act.description}</p><p className="text-[11px] text-text-muted mt-0.5">{act.user} &middot; {new Date(act.created_at).toLocaleDateString()}</p></div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'notes' && (
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <textarea placeholder="Add a note about this lead..." value={newNote} onChange={e => setNewNote(e.target.value)} rows={3} className="input-field h-auto py-2.5 resize-none flex-1" data-testid="add-note-input" />
                    <button onClick={handleAddNote} className="btn-primary self-end text-sm" data-testid="add-note-btn">Add</button>
                  </div>
                </div>
              )}

              {tab === 'outreach' && (() => {
                const oc = outreach?.outreach_copy || lead.outreach_copy;
                const reach = outreach?.reachability || lead.reachability;
                if (!oc) {
                  return (
                    <div className="text-center py-10" data-testid="outreach-empty">
                      <i className="fa-solid fa-bullhorn text-3xl text-fuchsia-400 mb-3" />
                      <p className="text-sm text-text-muted mb-4">Run the free pipeline to confirm the owner, verify an email, find a phone, and auto-draft outreach.</p>
                      <button onClick={handleReachOut} disabled={reachOut} className="btn-primary text-sm" data-testid="reach-out-btn-empty">
                        {reachOut ? 'Working...' : 'Reach Out (Free)'}
                      </button>
                    </div>
                  );
                }
                const copyText = (label, text) => { navigator.clipboard.writeText(text || ''); toast.success(`${label} copied`); };
                const Channel = ({ ok, icon, label, detail }) => (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold ${ok ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-border bg-surface-alt text-text-muted'}`} data-testid={`channel-${label.toLowerCase()}`}>
                    <i className={`fa-solid ${icon}`} /> {label}{ok && detail ? <span className="font-normal opacity-80">· {detail}</span> : ok ? '' : ' · n/a'}
                  </div>
                );
                return (
                  <div className="space-y-5" data-testid="outreach-panel">
                    <div className="flex flex-wrap gap-2">
                      <Channel ok={reach?.mailable} icon="fa-envelope-open-text" label="Mail" detail={reach?.is_absentee ? 'absentee' : 'owner-occupied'} />
                      <Channel ok={reach?.emailable} icon="fa-envelope" label="Email" detail={reach?.top_email} />
                      <Channel ok={reach?.callable} icon="fa-phone" label="Phone" detail={reach?.best_phone} />
                      <span className="text-[10px] px-2 py-1 rounded-md bg-fuchsia-100 text-fuchsia-700 font-bold self-center">
                        Copy {oc.generated_by === 'ai' ? 'AI-generated' : 'template'}
                      </span>
                    </div>
                    {[
                      { key: 'email', icon: 'fa-envelope', title: 'Email', subj: oc.email?.subject, body: oc.email?.body },
                      { key: 'mail', icon: 'fa-envelope-open-text', title: 'Direct Mail Letter', subj: oc.mail?.headline, body: oc.mail?.body },
                      { key: 'sms', icon: 'fa-comment-sms', title: 'SMS / Text', subj: null, body: oc.sms?.body },
                    ].map(c => (
                      <div key={c.key} className="rounded-xl border border-border p-4" data-testid={`outreach-${c.key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-text-primary"><i className={`fa-solid ${c.icon} mr-1.5 text-fuchsia-600`} />{c.title}</span>
                          <button onClick={() => copyText(c.title, (c.subj ? c.subj + '\n\n' : '') + (c.body || ''))} className="text-[11px] font-semibold text-fuchsia-600 hover:underline" data-testid={`copy-${c.key}-btn`}>
                            <i className="fa-regular fa-copy mr-1" />Copy
                          </button>
                        </div>
                        {c.subj && <p className="text-xs font-semibold text-text-primary mb-1">{c.subj}</p>}
                        <p className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">{c.body}</p>
                      </div>
                    ))}
                    {(outreach?.email_candidates?.length > 0 || outreach?.phone_candidates?.length > 0) && (
                      <div className="text-[11px] text-text-muted">
                        {outreach.email_candidates?.length > 0 && <div><b>Email guesses:</b> {outreach.email_candidates.join(', ')}</div>}
                        {outreach.phone_candidates?.length > 0 && <div><b>Phone candidates:</b> {outreach.phone_candidates.join(', ')}</div>}
                        <div className="mt-1 opacity-80"><b>Sources:</b> {sanitizeSources(outreach.data_sources).join(' · ')}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-full xl:w-[340px] space-y-4">
          <div className="card-interactive p-6 text-center">
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">Lead Score</h3>
            <div className="relative w-32 h-32 mx-auto mb-3">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E4E8" strokeWidth="7" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={scoreGrad} strokeWidth="7" strokeDasharray={`${(scorePct / 100) * 264} 264`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading text-3xl font-bold text-text-primary tabular-nums" data-testid="lead-score">{scorePct}</span>
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{scoreLabel}</span>
              </div>
            </div>
          </div>

          {/* Data Accuracy Score */}
          {lead.accuracy_score > 0 && (
            <div className="card-interactive p-4" data-testid="accuracy-score">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className={`fa-solid fa-shield-check ${lead.accuracy_score >= 70 ? 'text-primary' : lead.accuracy_score >= 40 ? 'text-amber-500' : 'text-text-muted'}`} />
                  <span className="text-xs font-bold text-text-primary">Data Accuracy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 rounded-full bg-nardo-light overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${lead.accuracy_score >= 70 ? 'bg-primary' : lead.accuracy_score >= 40 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${lead.accuracy_score}%` }} />
                  </div>
                  <span className={`text-sm font-bold font-mono ${lead.accuracy_score >= 70 ? 'text-primary' : lead.accuracy_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>{lead.accuracy_score}%</span>
                </div>
              </div>
            </div>
          )}

          <div className="card-interactive p-6">
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4 flex items-center gap-2"><i className="fa-solid fa-solar-panel text-primary" />Solar Analysis</h3>
            <div className="space-y-3">
              {[
                { icon: 'fa-sun', l: 'Solar Rating', v: lead.solar_potential, badge: true },
                { icon: 'fa-bolt', l: 'Annual Production', v: lead.annual_kwh ? `${lead.annual_kwh.toLocaleString()} kWh` : 'N/A' },
                { icon: 'fa-gauge-high', l: 'System Size', v: lead.recommended_system_kw ? `${lead.recommended_system_kw} kW` : 'N/A' },
                { icon: 'fa-piggy-bank', l: 'Yearly Savings', v: lead.estimated_savings ? `$${lead.estimated_savings.toLocaleString()}` : 'N/A' },
                { icon: 'fa-house-chimney', l: 'Roof Age', v: lead.roof_age ? `${lead.roof_age} years` : 'N/A' },
                { icon: 'fa-money-bill-trend-up', l: 'Project Value', v: lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : 'N/A' },
              ].map(item => (
                <div key={item.l} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-text-muted flex items-center gap-2"><i className={`fa-solid ${item.icon} w-4 text-center`} />{item.l}</span>
                  {item.badge ? (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${item.v === 'High' ? 'bg-primary/10 text-primary' : item.v === 'Medium' ? 'bg-yellow-50 text-yellow-700' : 'bg-destructive-light text-destructive'}`}>{item.v || 'N/A'}</span>
                  ) : (
                    <span className="text-sm font-semibold text-text-primary font-mono tabular-nums">{item.v}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Intelligence Section */}
          {(contactData || lead.enrichment) && (() => {
            const cd = contactData || lead.enrichment || {};
            return (
              <div className="card-interactive p-6 border-blue-200/30" data-testid="contact-enrichment">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-sm font-semibold text-text-primary flex items-center gap-2">
                    <i className="fa-solid fa-address-book text-blue-600" />Contact Intelligence
                    {lead.enrichment_version === 'v3' && <span className="text-[9px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-md">v3 Cross-Referenced</span>}
                  </h3>
                  {lead.accuracy_score > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 h-1.5 bg-surface-alt rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${lead.accuracy_score >= 70 ? 'bg-primary' : lead.accuracy_score >= 40 ? 'bg-secondary' : 'bg-destructive'}`} style={{ width: `${lead.accuracy_score}%` }} />
                      </div>
                      <span className={`text-xs font-bold tabular-nums ${lead.accuracy_score >= 70 ? 'text-primary' : lead.accuracy_score >= 40 ? 'text-secondary' : 'text-destructive'}`}>{lead.accuracy_score}%</span>
                    </div>
                  )}
                </div>

                {lead.owner_name && (
                  <div className="mb-3 p-3 rounded-xl bg-surface-alt border border-border">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1">Property Owner</p>
                    <p className="text-base font-bold text-text-primary">{lead.owner_name}</p>
                    {lead.is_absentee_owner && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-200">
                        <i className="fa-solid fa-arrow-right-from-bracket" /> Absentee Owner
                      </span>
                    )}
                  </div>
                )}

                {/* Cross-Reference Results */}
                {cd.cross_references && cd.cross_references.length > 0 && (
                  <div className="mb-3 p-3 rounded-xl bg-surface-alt border border-border">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">Cross-Reference Verification</p>
                    <div className="space-y-1.5">
                      {cd.cross_references.map((xref, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <i className={`fa-solid ${xref.verified ? 'fa-circle-check text-primary' : 'fa-circle-xmark text-destructive'} text-sm`} />
                          <span className="font-medium text-text-primary">{xref.source}</span>
                          <span className="text-text-muted">{xref.field}:</span>
                          <span className={`font-bold ${xref.verified ? 'text-primary' : 'text-destructive'}`}>
                            {xref.verified ? `MATCH (${Math.round(xref.match_score * 100)}%)` : `NO MATCH (${Math.round(xref.match_score * 100)}%)`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Verification Steps */}
                {cd.verification_steps && cd.verification_steps.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Verification Log</p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {cd.verification_steps.map((step, i) => (
                        <p key={i} className="text-[11px] text-text-secondary flex items-start gap-1.5">
                          <i className={`fa-solid ${step.includes('VERIFIED') || step.includes('verified') ? 'fa-check text-primary' : step.includes('MISMATCH') ? 'fa-xmark text-destructive' : 'fa-minus text-text-muted'} text-[9px] mt-0.5 flex-shrink-0`} />
                          {step}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {cd.top_3_emails && cd.top_3_emails.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1.5">Likely Emails</p>
                    <div className="space-y-1">
                      {cd.top_3_emails.map((email, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <i className={`fa-solid fa-${i === 0 ? 'star text-amber-500' : 'envelope text-text-muted'}`} />
                          <span className={`font-mono ${i === 0 ? 'font-bold text-text-primary' : 'text-text-secondary'}`}>{email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cd.phone_intelligence && cd.phone_intelligence.area_code_primary && (
                  <div className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1.5">Phone Intelligence</p>
                    <p className="text-xs text-text-primary font-mono">Area Code: <span className="font-bold">{cd.phone_intelligence.area_code_primary}</span></p>
                    {cd.phone_intelligence.public_record_sources && cd.phone_intelligence.public_record_sources.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-[10px] text-text-muted font-semibold">Verified via Samaritan Solar Intelligence</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Property Details */}
                {cd.property_details && cd.property_details.attom_id && (
                  <div className="mb-3 p-3 rounded-xl bg-surface-alt border border-border">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-secondary mb-1.5">Property Details</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {cd.property_details.year_built && <div><span className="text-text-muted block">Built</span><span className="font-bold text-text-primary">{cd.property_details.year_built}</span></div>}
                      {cd.property_details.sqft && <div><span className="text-text-muted block">SqFt</span><span className="font-bold text-text-primary">{cd.property_details.sqft?.toLocaleString()}</span></div>}
                      {cd.property_details.bedrooms && <div><span className="text-text-muted block">Bed/Bath</span><span className="font-bold text-text-primary">{cd.property_details.bedrooms}/{cd.property_details.bathrooms}</span></div>}
                    </div>
                  </div>
                )}

                {/* Tax Assessment */}
                {cd.assessment && cd.assessment.assessed_value && (
                  <div className="mb-3 p-3 rounded-xl bg-surface-alt border border-border">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-secondary mb-1.5">Tax Assessment</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-text-muted block">Assessed</span><span className="font-bold text-text-primary">${cd.assessment.assessed_value?.toLocaleString()}</span></div>
                      {cd.assessment.market_value && <div><span className="text-text-muted block">Market</span><span className="font-bold text-text-primary">${cd.assessment.market_value?.toLocaleString()}</span></div>}
                      {cd.assessment.tax_amount && <div><span className="text-text-muted block">Annual Tax</span><span className="font-bold text-text-primary">${cd.assessment.tax_amount?.toLocaleString()}</span></div>}
                    </div>
                  </div>
                )}

                {cd.data_sources && cd.data_sources.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-border">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted mb-1">Verified By</p>
                    <div className="flex flex-wrap gap-1">
                      {sanitizeSources(cd.data_sources).map((src, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-surface-alt border border-border text-text-muted">{src}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* AI Outreach Strategy */}
          {lead.ai_outreach_strategy && (
            <div className="card-interactive p-6 border-purple-200/30" data-testid="ai-outreach">
              <h3 className="font-heading text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <i className="fa-solid fa-bullseye text-purple-600" />AI Outreach Strategy
              </h3>
              {lead.ai_outreach_strategy.outreach_strategy && (
                <p className="text-sm text-text-secondary mb-2">{lead.ai_outreach_strategy.outreach_strategy}</p>
              )}
              {lead.ai_outreach_strategy.best_contact_method && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase text-text-muted">Best Method:</span>
                  <span className="text-xs font-bold text-purple-600 capitalize">{lead.ai_outreach_strategy.best_contact_method.replace('_', ' ')}</span>
                </div>
              )}
              {lead.ai_outreach_strategy.personalization_hooks && lead.ai_outreach_strategy.personalization_hooks.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-bold uppercase text-text-muted">Conversation Starters</p>
                  {lead.ai_outreach_strategy.personalization_hooks.map((hook, i) => (
                    <p key={i} className="text-xs text-text-secondary flex items-start gap-1.5">
                      <i className="fa-solid fa-lightbulb text-amber-500 text-[9px] mt-1 flex-shrink-0" />{hook}
                    </p>
                  ))}
                </div>
              )}
              {lead.ai_outreach_strategy.pitch_angle && (
                <div className="mt-2 p-2 rounded-lg bg-purple-50/50">
                  <p className="text-[10px] font-bold uppercase text-purple-600 mb-0.5">Pitch Angle</p>
                  <p className="text-xs text-text-secondary">{lead.ai_outreach_strategy.pitch_angle}</p>
                </div>
              )}
            </div>
          )}

          {/* AI Enrichment Section */}
          {enrichment && (
            <div className="card-interactive p-6 border-primary/20 bg-gradient-to-b from-primary/5 to-transparent" data-testid="ai-enrichment">
              <h3 className="font-heading text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <i className="fa-solid fa-brain text-primary" />AI Sales Intelligence
              </h3>
              {enrichment.recommendation && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Recommendation</p>
                  <p className="text-sm text-text-secondary leading-relaxed">{enrichment.recommendation}</p>
                </div>
              )}
              {enrichment.best_approach && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Best Approach</p>
                  <p className="text-sm text-text-secondary">{enrichment.best_approach}</p>
                </div>
              )}
              {enrichment.talking_points && enrichment.talking_points.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">Talking Points</p>
                  <ul className="space-y-1.5">
                    {enrichment.talking_points.map((tp, i) => (
                      <li key={i} className="text-xs text-text-secondary flex items-start gap-2">
                        <i className="fa-solid fa-check text-primary text-[9px] mt-1 flex-shrink-0" />{tp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-primary/10">
                {enrichment.urgency && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase text-text-muted">Urgency:</span>
                    <span className={`text-xs font-bold capitalize ${enrichment.urgency === 'high' ? 'text-destructive' : enrichment.urgency === 'medium' ? 'text-secondary' : 'text-accent'}`}>{enrichment.urgency}</span>
                  </div>
                )}
                {enrichment.estimated_close_probability !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold uppercase text-text-muted">Close Prob:</span>
                    <span className="text-xs font-bold text-primary">{enrichment.estimated_close_probability}%</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
